import { createApi } from '@reduxjs/toolkit/query/react';
import { createBaseQueryWithReauth } from '@/services';
import { PaginatedRequest, PaginatedResponse } from '@/types';
import { API_BUSINESS_URL } from '@/utils';

export interface CommerceApi {
  id: string;
  name: string;
  code?: string;
}

export interface BranchApi {
  id: string;
  name: string;
  businessName?: string;
  commerceId: string;
  address?: string;
  addressId?: string;
  externalId?: string;
}

export interface CommercialServiceApi {
  id: string;
  name: string;
  description?: string;
  commercialName?: string;
  externalId?: string;
}

export interface CountryApi {
  id: string;
  name: string;
  code?: string;
}

export interface DepartmentApi {
  id: string;
  name: string;
  code?: string;
}

export interface CityApi {
  id: string;
  name: string;
  departmentId: string;
}

export interface NeighborhoodApi {
  id: string;
  name: string;
  cityId: string;
}

export interface ProductTypeApi {
  id: string;
  name: string;
  description?: string;
}

export interface StatusApi {
  id: string;
  name: string;
  color?: string;
}

export interface SizeApi {
  id: string;
  name: string;
  description?: string;
}

export interface ClassificationApi {
  id: string;
  name: string;
  description?: string;
}

export interface ComplexityApi {
  id: string;
  name: string;
  description?: string;
}

export interface TypeApi {
  id: string;
  name: string;
  description?: string;
}

export interface ChannelApi {
  id: string;
  name: string;
  description?: string;
}

export interface TimeSlotApi {
  id: string;
  name: string;
  code: string;
  dateTimeFrom?: string;
  dateTimeTo?: string;
}

export interface ProductSizeApi {
  id: string;
  name: string;
  description?: string;
}

const shippingOptionsService = createApi({
  reducerPath: 'shippingOptionsApi',
  baseQuery: createBaseQueryWithReauth(`${API_BUSINESS_URL}`),
  tagTypes: [
    'Commerces',
    'Branches',
    'CommercialServices',
    'Countries',
    'Departments',
    'Cities',
    'Neighborhoods',
    'ProductTypes',
    'Statuses',
    'Sizes',
    'Classifications',
    'Complexities',
    'Types',
    'Channels',
    'TimeSlots',
    'ProductSizes',
  ],
  endpoints: (builder) => ({
    getCountries: builder.query<
      PaginatedResponse<CountryApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/countries',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: ['Countries'],
    }),

    // Comercios
    getCommerces: builder.query<
      PaginatedResponse<CommerceApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/commerces',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: ['Commerces'],
    }),

    getBranches: builder.query<
      PaginatedResponse<BranchApi>,
      { paginated: PaginatedRequest; commerceId?: string; search?: string }
    >({
      query: ({ paginated, commerceId, search }) => ({
        url: '/branches',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(commerceId && { commerceId }),
          ...(search && search.trim() !== '' && { search: search.trim() }),
        },
      }),
      providesTags: ['Branches'],
    }),

    getCommercialServices: builder.query<
      PaginatedResponse<CommercialServiceApi>,
      { paginated: PaginatedRequest; commerceId?: string; search?: string }
    >({
      query: ({ paginated, commerceId, search }) => ({
        url: '/commercial-service',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(commerceId && { commerceId }),
          ...(search && { search }),
        },
      }),
      providesTags: ['CommercialServices'],
    }),

    getDepartments: builder.query<
      PaginatedResponse<DepartmentApi>,
      { paginated: PaginatedRequest; countryId?: string; search?: string }
    >({
      query: ({ paginated, countryId, search }) => ({
        url: '/states-or-departments',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(countryId && { countryId }),
          ...(search && { search }),
        },
      }),
      providesTags: ['Departments'],
    }),

    getCities: builder.query<
      PaginatedResponse<CityApi>,
      { paginated: PaginatedRequest; departmentId?: string; search?: string }
    >({
      query: ({ paginated, departmentId, search }) => ({
        url: '/cities',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(departmentId && { stateOrDepartmentId: departmentId }),
          ...(search && { search }),
        },
      }),
      providesTags: (_, __, { departmentId }) => [
        { type: 'Cities', id: departmentId || 'all' },
        'Cities',
      ],
    }),

    getNeighborhoods: builder.query<
      PaginatedResponse<NeighborhoodApi>,
      { paginated: PaginatedRequest; cityId?: string; search?: string }
    >({
      query: ({ paginated, cityId, search }) => ({
        url: '/neighborhoods',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(cityId && { cityId }),
          ...(search && { search }),
        },
      }),
      providesTags: ['Neighborhoods'],
    }),

    getProductTypes: builder.query<
      PaginatedResponse<ProductTypeApi>,
      { paginated: PaginatedRequest; search?: string }
    >({
      query: ({ paginated, search }) => ({
        url: '/product-types',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
          ...(search && { search }),
        },
      }),
      providesTags: ['ProductTypes'],
    }),

    getStatuses: builder.query<
      PaginatedResponse<StatusApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/statuses',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Statuses'],
    }),

    getSizes: builder.query<
      PaginatedResponse<SizeApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/shipping-sizes',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Sizes'],
    }),

    getClassifications: builder.query<
      PaginatedResponse<ClassificationApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/classifications',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Classifications'],
    }),

    getComplexities: builder.query<
      PaginatedResponse<ComplexityApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/complexities',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Complexities'],
    }),

    getTypes: builder.query<
      PaginatedResponse<TypeApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/types',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Types'],
    }),

    getChannels: builder.query<
      PaginatedResponse<ChannelApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/channels',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['Channels'],
    }),

    getTimeSlots: builder.query<
      PaginatedResponse<TimeSlotApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/time-slots',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['TimeSlots'],
    }),

    getProductSizes: builder.query<
      PaginatedResponse<ProductSizeApi>,
      { paginated: PaginatedRequest }
    >({
      query: ({ paginated }) => ({
        url: '/product-sizes',
        params: {
          limit: paginated.pageSize,
          offset: (paginated.page - 1) * paginated.pageSize,
        },
      }),
      providesTags: ['ProductSizes'],
    }),
  }),
});

export default shippingOptionsService;

export const {
  useGetCountriesQuery,
  useGetCommercesQuery,
  useGetBranchesQuery,
  useGetCommercialServicesQuery,
  useGetDepartmentsQuery,
  useGetCitiesQuery,
  useGetNeighborhoodsQuery,
  useGetProductTypesQuery,
  useGetStatusesQuery,
  useGetSizesQuery,
  useGetClassificationsQuery,
  useGetComplexitiesQuery,
  useGetTypesQuery,
  useGetChannelsQuery,
  useGetTimeSlotsQuery,
  useGetProductSizesQuery,
} = shippingOptionsService;
